/**
 * ZoomableImage
 *
 * Shared "+" hover hint + full-screen lightbox with a hover magnifier lens
 * (Amazon-style: moving the mouse over the image shows a zoomed panel
 * beside it). Used anywhere a thumbnail needs to zoom to full screen:
 * EventDetail Info tab hero image, EventDetail Images tab, and the
 * Flyer History sub-tabs (Venue/Artist/Promoter/Festival).
 *
 * Exports:
 *   ZoomHint     — small "+" overlay icon; parent controls visibility via `visible`
 *   ImageLightbox — full-screen modal; props: src, alt, caption, viewShowTo, viewLinkLabel, onClose
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PANEL_SIZE = 420;   // px, magnifier panel dimensions
const ZOOM = 2.5;         // magnification factor

// ── "+" hover hint icon ────────────────────────────────────────────────────
export function ZoomHint({ visible = true, style }) {
    return (
        <div style={{ ...S.zoomHint, opacity: visible ? 1 : 0, ...style }}>
            <span style={S.zoomHintIcon}>+</span>
        </div>
    );
}

// ── Full-screen lightbox with magnifier ────────────────────────────────────
export function ImageLightbox({ src, alt, caption, viewShowTo, viewLinkLabel = 'View Show →', onClose }) {
    const [isTouch] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none)').matches
    );
    const [lens, setLens] = useState(null); // { boxLeft, boxTop, boxSize, bgX, bgY, bgW, bgH }
    const imgRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleMouseMove = (e) => {
        if (isTouch || !imgRef.current) return;
        const rect = imgRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
            setLens(null);
            return;
        }
        const boxSize = PANEL_SIZE / ZOOM;
        const boxLeft = Math.min(Math.max(x - boxSize / 2, 0), rect.width - boxSize);
        const boxTop  = Math.min(Math.max(y - boxSize / 2, 0), rect.height - boxSize);
        setLens({
            boxLeft, boxTop, boxSize,
            bgX: -(boxLeft * ZOOM),
            bgY: -(boxTop * ZOOM),
            bgW: rect.width * ZOOM,
            bgH: rect.height * ZOOM,
        });
    };

    const showMagnifier = !isTouch;

    return (
        <div style={S.overlay} onClick={onClose}>
            <div style={S.stage} onClick={(e) => e.stopPropagation()}>
                <div style={{ ...S.imageCol, ...(showMagnifier ? {} : S.imageColTouch) }}>
                    <div
                        style={S.imageWrap}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={() => setLens(null)}
                    >
                        <img
                            ref={imgRef}
                            src={src}
                            alt={alt || ''}
                            style={showMagnifier ? S.imgDesktop : S.imgTouch}
                        />
                        {showMagnifier && lens && (
                            <div
                                style={{
                                    ...S.lensBox,
                                    left: lens.boxLeft,
                                    top: lens.boxTop,
                                    width: lens.boxSize,
                                    height: lens.boxSize,
                                }}
                            />
                        )}
                    </div>
                    {(caption || viewShowTo) && (
                        <div style={S.footer}>
                            {caption && <div style={S.caption}>{caption}</div>}
                            {viewShowTo && (
                                <Link to={viewShowTo} style={S.viewShowLink} onClick={onClose}>
                                    {viewLinkLabel}
                                </Link>
                            )}
                        </div>
                    )}
                </div>

                {showMagnifier && (
                    <div
                        style={{
                            ...S.magnifierPanel,
                            ...(lens
                                ? {
                                    backgroundImage: `url(${src})`,
                                    backgroundSize: `${lens.bgW}px ${lens.bgH}px`,
                                    backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
                                    backgroundRepeat: 'no-repeat',
                                }
                                : {}),
                        }}
                    >
                        {!lens && <div style={S.magnifierHint}>Hover the image to zoom in</div>}
                    </div>
                )}
            </div>

            <button onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>
    );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const S = {
    zoomHint: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '26px',
        height: '26px',
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.6)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'opacity 0.15s ease',
        pointerEvents: 'none',
        zIndex: 2,
    },
    zoomHintIcon: { fontSize: '15px', fontWeight: '700', lineHeight: 1 },

    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000, padding: '24px',
    },
    stage: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start',
        justifyContent: 'center', gap: '20px', maxWidth: '95vw', maxHeight: '90vh',
    },
    imageCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 },
    imageColTouch: { maxWidth: '90vw' },
    imageWrap: { position: 'relative', display: 'inline-block', cursor: 'crosshair' },
    imgDesktop: { display: 'block', maxWidth: 'min(50vw, 520px)', maxHeight: '78vh', objectFit: 'contain', borderRadius: '8px' },
    imgTouch: { display: 'block', maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' },
    lensBox: {
        position: 'absolute', border: '2px solid #bb86fc', background: 'rgba(187,134,252,0.15)',
        pointerEvents: 'none', borderRadius: '2px',
    },
    magnifierPanel: {
        width: `${PANEL_SIZE}px`, height: `${PANEL_SIZE}px`, flexShrink: 0,
        background: '#1a1a1a', border: '1px solid #333', borderRadius: '8px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    magnifierHint: { color: '#666', fontSize: '13px', textAlign: 'center', padding: '0 24px' },

    footer: { marginTop: '10px', textAlign: 'center' },
    caption: { color: '#e0e0e0', fontSize: '13px', marginBottom: '4px' },
    viewShowLink: {
        display: 'inline-block', color: '#bb86fc', fontSize: '13px', fontWeight: '600',
        textDecoration: 'none', border: '1px solid #bb86fc', borderRadius: '6px', padding: '6px 14px',
    },

    closeBtn: {
        position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.6)',
        border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', borderRadius: '6px',
        padding: '4px 12px', zIndex: 3001,
    },
};
